/** Tipos do confronto de notas. Campo ausente no XML é null — nunca string vazia nem zero. */

export type TabelaIcms = 'ICMS_CST' | 'ICMS_CSOSN';

export type IcmsLido = {
  grupo: string;
  tabela: TabelaIcms;
  cst: string | null;
  orig: string | null;
  modBC: string | null;
  modBCST: string | null;
  motDesICMS: string | null;
  motDesICMSST: string | null;
  /** Valores de ST retida anteriormente informados (vBCSTRet / vICMSSTRet). */
  temStRetido: boolean;
  /** MVA-ST informada (%), quando modBCST = 4 (margem de valor agregado). */
  pMVAST: number | null;
  /** Base, redução (%), alíquota e valor do ICMS-ST, quando o grupo os declara (CST 10/30/70, CSOSN 201/202/203/900). */
  vBCST: number | null;
  pRedBCST: number | null;
  pICMSST: number | null;
  vICMSST: number | null;
  /** Base, alíquota e valor do ICMS próprio, quando o grupo os declara (ex.: ausentes em isenção/não tributado). */
  vBC: number | null;
  pICMS: number | null;
  vICMS: number | null;
  /**
   * Diferimento (grupo ICMS51 e, desde a NT 2023.001, ICMS53/ICMS90): valor do ICMS da operação antes de diferir,
   * percentual diferido e valor diferido. Com eles, vICMS = vICMSOp − vICMSDif, e não vBC × pICMS.
   */
  vICMSOp: number | null;
  pDif: number | null;
  vICMSDif: number | null;
};

/** Base, alíquota (%) e valor de um tributo simples (IPI, PIS, COFINS) — null quando o subgrupo não os declara. */
export type TributoValorLido = { cst: string | null; vBC: number | null; pAliquota: number | null; vTributo: number | null };

/** Partilha do ICMS entre UF de origem e destino (EC 87/2015), grupo ICMSUFDest do item. */
export type IcmsUfDestLido = {
  vBCUFDest: number | null;
  pFCPUFDest: number | null;
  pICMSUFDest: number | null;
  pICMSInter: number | null;
  pICMSInterPart: number | null;
  vFCPUFDest: number | null;
  vICMSUFDest: number | null;
};

export type ItemLido = {
  nItem: number;
  cProd: string | null;
  xProd: string | null;
  ncm: string | null;
  cest: string | null;
  /** Código de benefício fiscal (cBenef) do item, na UF do emitente. */
  cBenef: string | null;
  cfop: string | null;
  vProd: number | null;
  /**
   * prod/indTot: true quando o vProd do item compõe o total da nota (ICMSTot/vProd ou, se serviço, ISSQNtot/vServ).
   * Ausente = true (campo obrigatório no leiaute 4.00).
   */
  indTot: boolean;
  /** Quantidade, unidade e valor unitário comerciais (prod/qCom, uCom, vUnCom) — para exibição no DANFE. */
  qCom: number | null;
  uCom: string | null;
  vUnCom: number | null;
  /**
   * Campos monetários aditivos de prod/ (desconto, frete, seguro, outras despesas, imposto de
   * importação) — ao contrário dos campos de tributo, a ausência aqui significa zero: são elementos
   * opcionais do schema, não uma configuração tributária diferente.
   */
  vDesc: number | null;
  vFrete: number | null;
  vSeg: number | null;
  vOutro: number | null;
  vII: number | null;
  icms: IcmsLido | null;
  icmsUFDest: IcmsUfDestLido | null;
  ipi: (TributoValorLido & { cEnq: string | null }) | null;
  pis: TributoValorLido | null;
  cofins: TributoValorLido | null;
  ibscbs: IbsCbsLido | null;
  /** Grupo ISSQN (NF-e conjugada): o item é serviço, não tem grupo ICMS e fecha contra o ISSQNtot. */
  issqn: IssqnLido | null;
};

/** Grupo ISSQN do item: base, alíquota (%), valor, município de incidência, item da LC 116/03 e exigibilidade. */
export type IssqnLido = {
  vBC: number | null;
  vAliq: number | null;
  vISSQN: number | null;
  cMunFG: string | null;
  cListServ: string | null;
  indISS: string | null;
};

/**
 * Grupo IBSCBS do item (reforma tributária, NT 2025.002): CST, cClassTrib e, quando o item é tributado, a base
 * (gIBSCBS/vBC) e alíquota/valor de IBS estadual (gIBSUF), IBS municipal (gIBSMun) e CBS (gCBS). `pRed*` é o
 * percentual de redução de alíquota (gRed), quando declarado.
 */
export type IbsCbsLido = {
  cst: string | null;
  cClassTrib: string | null;
  vBC: number | null;
  pIBSUF: number | null;
  vIBSUF: number | null;
  pRedIBSUF: number | null;
  pIBSMun: number | null;
  vIBSMun: number | null;
  pRedIBSMun: number | null;
  pCBS: number | null;
  vCBS: number | null;
  pRedCBS: number | null;
};

/** Endereço de emitente/destinatário (enderEmit/enderDest) — usado só para exibição no DANFE. */
export type EnderecoLido = {
  xLgr: string | null;
  nro: string | null;
  xCpl: string | null;
  xBairro: string | null;
  cMun: string | null;
  xMun: string | null;
  uf: string | null;
  cep: string | null;
  fone: string | null;
};

/** Grupo transp — modal, transportador e o primeiro volume, para o DANFE. */
export type TransporteLido = {
  modFrete: string | null;
  transporta: { doc: string | null; xNome: string | null; ie: string | null; uf: string | null } | null;
  vol: { qVol: string | null; esp: string | null; pesoL: string | null; pesoB: string | null } | null;
};

/** Uma forma de pagamento (detPag) — tPag e vPag. */
export type PagamentoLido = { tPag: string | null; vPag: number | null };

/** Protocolo de autorização (protNFe, irmão de NFe dentro de nfeProc) — null quando o XML não é um nfeProc. */
export type ProtocoloLido = { nProt: string | null; dhRecbto: string | null };

/** Evento de cancelamento da NFS-e (pedRegEvento): 101101 cancelamento, 105102 cancelamento por substituição. */
export type EventoNfseLido = { chave: string; codigo: string; descricao: string | null; motivo: string | null; dhEvento: string | null };

/** Pessoa da NFS-e (tomador ou intermediário): documento (CNPJ, CPF ou NIF), nome, município (IBGE) e inscrição municipal. */
export type PessoaServicoLida = { doc: string | null; nome: string | null; cMun: string | null; im: string | null };

/**
 * Serviço da NFS-e do padrão nacional (leiaute SEFIN/ADN 1.01, Anexo I). A NFS-e tem um único serviço: os campos da DPS
 * (o que o emitente informou) e os calculados pelo sistema emissor (infNFSe/valores e infNFSe/IBSCBS) ficam lado a lado,
 * porque as regras conferem um contra o outro. Códigos de domínio vêm como no XML (ex.: tribISSQN '1').
 */
export type ServicoLido = {
  /** Código de Tributação Nacional (6 dígitos: item e subitem da LC 116 + desdobro nacional). */
  cTribNac: string | null;
  xTribNac: string | null;
  cTribMun: string | null;
  xTribMun: string | null;
  /** NBS (9 dígitos). */
  cNBS: string | null;
  xNBS: string | null;
  xDescServ: string | null;
  /** Município (IBGE) ou país (ISO) da prestação; município de incidência do ISSQN (calculado pelo sistema). */
  cLocPrestacao: string | null;
  cPaisPrestacao: string | null;
  cLocIncid: string | null;
  xLocIncid: string | null;
  /** Data de competência (yyyy-mm-dd) — é ela que decide alíquota e vigência no ISS. */
  dCompet: string | null;
  serieDps: string | null;
  nDps: string | null;
  /** 100 gerada, 102 decisão judicial/administrativa, 103 avulsa, 107 MEI. */
  cStat: string | null;
  dhProc: string | null;
  /** 1 prestador, 2 tomador, 3 intermediário. */
  tpEmit: string | null;
  /** Chave da NFS-e substituída, quando esta é uma substituição. */
  chSubstda: string | null;
  /** Evento de cancelamento (101101, ou 105102 por substituição) recebido junto com a nota; null = sem cancelamento no lote. */
  cancelamento?: EventoNfseLido | null;
  prestador: { im: string | null; opSimpNac: string | null; regApTribSN: string | null; regEspTrib: string | null };
  tomador: PessoaServicoLida | null;
  intermediario: PessoaServicoLida | null;
  vServ: number | null;
  vDescIncond: number | null;
  vDescCond: number | null;
  /** Dedução/redução da base informada na DPS (pDR ou vDR) e a calculada pelo sistema (vCalcDR). */
  pDR: number | null;
  vDR: number | null;
  vCalcDR: number | null;
  /** Benefício municipal: tipo (1 isenção, 2/3 redução da base, 4 alíquota diferenciada), valor informado e calculado. */
  nBM: string | null;
  tpBM: string | null;
  vRedBCBM: number | null;
  vCalcBM: number | null;
  /** 1 tributável, 2 imunidade, 3 exportação, 4 não incidência. */
  tribISSQN: string | null;
  tpImunidade: string | null;
  /** Exigibilidade suspensa (1 decisão judicial, 2 processo administrativo). */
  tpSusp: string | null;
  /** 1 não retido, 2 retido pelo tomador, 3 retido pelo intermediário. */
  tpRetISSQN: string | null;
  /** Alíquota informada na DPS e a aplicada pelo sistema; base, ISS, total retido e líquido calculados (infNFSe/valores). */
  pAliq: number | null;
  pAliqAplic: number | null;
  vBC: number | null;
  vISSQN: number | null;
  vTotalRet: number | null;
  vLiq: number | null;
  /** tribFed/piscofins: tpRetPisCofins diz o que foi retido (0 nada; 1 PIS/COFINS; 3 PIS/COFINS/CSLL...). */
  tpRetPisCofins: string | null;
  vRetCP: number | null;
  vRetIRRF: number | null;
  vRetCSLL: number | null;
  /** IBS/CBS (NT 007): o informado na DPS e os valores calculados pelo sistema (infNFSe/IBSCBS). */
  ibscbs: {
    cIndOp: string | null;
    finNFSe: string | null;
    indFinal: string | null;
    indDest: string | null;
    cLocalidadeIncid: string | null;
    vBC: number | null;
    vCalcReeRepRes: number | null;
    pAliqEfetUF: number | null;
    pAliqEfetMun: number | null;
    pAliqEfetCBS: number | null;
    vIBSTot: number | null;
    vTotNF: number | null;
  } | null;
};

export type NotaLida = {
  arquivo: string;
  chave: string;
  /** '55' NF-e, '65' NFC-e, 'NFSE' NFS-e do padrão nacional. */
  modelo: string | null;
  serie: string | null;
  numero: string | null;
  /** Data e hora como vieram no XML (com fuso). */
  dhEmissao: string | null;
  /** yyyy-mm-dd da emissão, no fuso do próprio XML. */
  dataEmissao: string | null;
  natOp: string | null;
  emit: { cnpj: string | null; nome: string | null; uf: string | null; crt: string | null; ie: string | null; enderEmit: EnderecoLido | null };
  dest: { doc: string | null; nome: string | null; uf: string | null; indIEDest: string | null; ie: string | null; enderDest: EnderecoLido | null };
  tpNF: string | null;
  finNFe: string | null;
  idDest: string | null;
  indFinal: string | null;
  indPres: string | null;
  vNF: number | null;
  /** Totais declarados em total/ICMSTot — comparados à soma dos itens, não recalculados. */
  totais: { vProd: number | null; vBC: number | null; vICMS: number | null; vIPI: number | null; vPIS: number | null; vCOFINS: number | null };
  /** Totais de total/ISSQNtot (NF-e conjugada): serviços, ISS e PIS/COFINS sobre serviços. null quando a nota não tem o grupo. */
  totaisIssqn: { vServ: number | null; vBC: number | null; vISS: number | null; vPIS: number | null; vCOFINS: number | null } | null;
  itens: ItemLido[];
  transp: TransporteLido | null;
  pag: PagamentoLido[];
  protocolo: ProtocoloLido | null;
  infAdic: { infCpl: string | null } | null;
  /**
   * NFS-e: o serviço da nota. Presente só em modelo 'NFSE' — aí `emit` é o prestador, `dest` o tomador, e a nota tem um
   * item (o serviço) com os grupos `issqn`, `pis`, `cofins` e `ibscbs` preenchidos a partir dela, para as regras comuns.
   */
  servico?: ServicoLido | null;
};

export const MODELO_NFSE = 'NFSE';
export const ehNfse = (nota: Pick<NotaLida, 'modelo'>) => nota.modelo === MODELO_NFSE;

export type ArquivoRejeitado = { arquivo: string; motivo: string };

export type Resultado = 'CONFORME' | 'DEPENDE' | 'DIVERGENTE';
export type Severidade = 'erro' | 'alerta' | 'info';

export type Achado = {
  regra: string;
  resultado: Exclude<Resultado, 'CONFORME'>;
  severidade: Severidade;
  mensagem: string;
  encontrado?: string | null;
  esperado?: string | null;
  /** Observação fiscal do cliente que alcança este achado (observacoes.ts). */
  observacaoId?: string | null;
  /** Efeito de regime especial do cliente que alcança este achado (fiscal_regime_especial_efeito). */
  regimeEfeitoId?: string | null;
  /** A observação justifica o achado: ele continua na lista, mas não conta como erro/alerta nem piora o resultado. */
  justificado?: boolean;
};

export type ItemAvaliado = {
  item: ItemLido;
  cenarioChave: string;
  naturezaCodigo: string | null;
  achados: Achado[];
  pior: Resultado;
};

export type NotaAvaliada = {
  nota: NotaLida;
  achados: Achado[];
  itens: ItemAvaliado[];
  pior: Resultado;
};

export type CenarioResumo = {
  chave: string;
  /** Modelo das notas do cenário ('NFSE' = serviço); a chave do cenário já separa NF-e de NFS-e. */
  modelo?: string | null;
  cfop: string | null;
  /** NFS-e: código de tributação nacional do serviço (no lugar do CFOP). */
  servico?: string | null;
  naturezaCodigo: string | null;
  qtdItens: number;
  qtdNotas: number;
  /** NCMs dos itens (NF-e) ou NBS dos serviços (NFS-e). */
  ncms: string[];
  vProd: number;
  pior: Resultado;
  regras: string[];
};

export type ResultadoConfronto = {
  versaoMotor: string;
  notas: NotaAvaliada[];
  rejeitados: ArquivoRejeitado[];
  cenarios: CenarioResumo[];
  /** erros/alertas/infos não incluem os achados justificados por observação do cliente — esses vão em `justificados`. */
  totais: { arquivos: number; notas: number; itens: number; cenarios: number; erros: number; alertas: number; infos: number; justificados: number };
};
